import { useQuery } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';

import { api } from '../lib/api';

/**
 * Shown on every page while the store is suspended: since when, the reason the
 * admin gave, what it means for the seller, and the way to appeal it. Without
 * it a suspended seller only saw actions failing with "not approved".
 */
export function StoreSuspendedBanner() {
  const store = useQuery({
    queryKey: ['vendor-store'],
    queryFn: () => api.vendors.getMyStore(),
    staleTime: 60_000,
  });
  if (store.data?.status !== 'SUSPENDED') return null;

  const since = store.data.suspendedAt
    ? new Date(store.data.suspendedAt).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null;

  return (
    <div
      role="alert"
      className="mb-6 flex flex-col gap-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-900 md:flex-row md:items-start md:p-5"
    >
      <ShieldAlert className="h-6 w-6 shrink-0 text-red-600" aria-hidden />
      <div className="min-w-0 flex-1 space-y-1.5 text-sm">
        <p className="text-base font-bold">
          Your store has been suspended{since ? ` since ${since}` : ''}
        </p>
        {store.data.latestReviewNote && (
          <p>
            <span className="font-semibold">Reason:</span> {store.data.latestReviewNote}
          </p>
        )}
        <p className="text-red-800">
          Your products are hidden from customers and no new orders can be placed. You cannot add or
          edit products, process orders, book couriers or request payouts until the store is
          reinstated. Raise a ticket to tell us what you have fixed or to appeal.
        </p>
      </div>
      <Link
        to="/support?topic=suspension"
        className="shrink-0 rounded-xl bg-red-600 px-4 py-2.5 text-center text-sm font-bold text-white no-underline transition-colors hover:bg-red-700"
      >
        Raise a ticket
      </Link>
    </div>
  );
}
